/* eslint-disable react/no-unused-state */
import PropTypes from 'prop-types';
import { PureComponent } from 'react';

import CheckMarkFilledIcon from 'Component/CheckMarkFilledIcon';
import { FieldNumberContainer } from 'Component/FieldNumber/FieldNumber.container';
import FieldSelectContainer from 'Component/FieldSelect/FieldSelect.container';
import LockIcon from 'Component/LockIcon';
import TextPlaceholder from 'Component/TextPlaceholder';
import { MixType } from 'Type/Common.type';
import {
    EventsType,
    FieldAttrType,
    LabelType,
    OptionType,
    ValidationRuleType
} from 'Type/Field.type';
import { noopFn } from 'Util/Common';

import { GREEN_KEY, M_KEY, XS_KEY } from '../Icon/Icon.config';
import {
    FIELD_TYPE, FIX_SIZE,
    FULL_SIZE, HEIGHT_LARGE, HEIGHT_SMALL
} from './Field.config';

import './Field.style.scss';

/**
 * Field
 * @class Field
 * @namespace Bodypwa/Component/Field/Component */
export class FieldComponent extends PureComponent {
    static propTypes = {
        // Field attributes
        type: PropTypes.oneOf(Object.values(FIELD_TYPE)).isRequired,
        attr: FieldAttrType.isRequired,
        events: EventsType.isRequired,
        isDisabled: PropTypes.bool.isRequired,
        setRef: PropTypes.func.isRequired,
        mix: MixType,
        options: PropTypes.arrayOf(OptionType).isRequired,
        isSkeletonCheckBoxOrRadio: PropTypes.bool.isRequired,
        isDisabledSelected: PropTypes.bool.isRequired,
        isReadOnly: PropTypes.bool.isRequired,
        size: PropTypes.oneOf([FIX_SIZE, FULL_SIZE]),
        isToggle: PropTypes.bool,
        validationRule: ValidationRuleType,
        isLast: PropTypes.bool.isRequired,
        isChecked: PropTypes.bool.isRequired,
        // Validation
         
        showErrorAsLabel: PropTypes.bool.isRequired,
        validationResponse: PropTypes.oneOfType([
            PropTypes.bool,
            PropTypes.object
        ]),
        // Labels
        label: LabelType.isRequired,
        subLabel: PropTypes.string.isRequired,
        isLoading: PropTypes.bool,
        // Placeholder
        placeholder: PropTypes.string,
        className: PropTypes.string,
        fontClass: PropTypes.string.isRequired,
        heightSize: PropTypes.oneOf([HEIGHT_SMALL, HEIGHT_LARGE]),
        textareaPlaceholderClass: PropTypes.string,
        isErrorMessage: PropTypes.bool,
        errorMessagePosition: PropTypes.string,
        isHiddenCheckIcon: PropTypes.bool,
        labelClass: PropTypes.string
    };

    static defaultProps = {
        validationResponse: null,
        isLoading: false,
        placeholder: '',
        mix: {},
        size: FULL_SIZE,
        isToggle: false,
        validationRule: {},
        className: '',
        heightSize: HEIGHT_LARGE,
        textareaPlaceholderClass: '',
        isErrorMessage: false,
        errorMessagePosition: '',
        isHiddenCheckIcon: false,
        labelClass: 'text-p14'
    };

    renderMap = {
        // Checkboxes & Radio
        [FIELD_TYPE.radio]: this.renderCheckboxOrRadio.bind(this),
        [FIELD_TYPE.checkbox]: this.renderCheckboxOrRadio.bind(this),
        [FIELD_TYPE.multi]: this.renderCheckboxOrRadio.bind(this),

        // Default input
        [FIELD_TYPE.email]: this.renderDefaultInput.bind(this),
        [FIELD_TYPE.text]: this.renderDefaultInput.bind(this),
        [FIELD_TYPE.time]: this.renderDefaultInput.bind(this),
        [FIELD_TYPE.dateTime]: this.renderDefaultInput.bind(this),
        [FIELD_TYPE.date]: this.renderDefaultInput.bind(this),
        [FIELD_TYPE.password]: this.renderDefaultInput.bind(this),
        [FIELD_TYPE.submit]: this.renderDefaultInput.bind(this),

        // Custom fields
        [FIELD_TYPE.select]: this.renderSelect.bind(this),
        [FIELD_TYPE.textarea]: this.renderTextArea.bind(this),
        [FIELD_TYPE.button]: this.renderButton.bind(this),
        [FIELD_TYPE.number]: this.renderNumber.bind(this)

    };

    constructor(props) {
        super(props);
        this.state = {
            focused: false,
            value: '',
            errorMessage: '',
            isValidEmail: false,
            isValid: true,
            showError: false
        };
    }

    //#region INPUT TYPE RENDER
    renderDefaultInput() {
        const {
            type,
            setRef,
            attr,
            attr: { id },
            events,
            isDisabled,
            validationResponse,
            isReadOnly,
            size,
            placeholder,
            isLoading,
            className,
            isHiddenCheckIcon
        } = this.props;

        if (isLoading) {
            return <TextPlaceholder height="button" length="button" />;
        }

        return (
            <div
                block="FieldInput"
                elem="InputContainer"
                mods={ { size } }
            >
                <input
                    className={ className }
                    ref={ (elem) => setRef(elem) }
                    type={ type }
                    readOnly={ isReadOnly }
                    disabled={ isDisabled }
                    placeholder={ placeholder }
                    { ...attr }
                    { ...events }
                />
                { this.renderLabel() }
                { !isHiddenCheckIcon && validationResponse === true && id !== 'couponCode' && (
                    <div block="FieldInput" elem="ValidIcon">
                        <CheckMarkFilledIcon
                            size={ M_KEY }
                            color={ GREEN_KEY }
                        />
                    </div>
                ) }
            </div>
        );
    }

    // Renders fields label above field
    renderLabel() {
        const { type, label, attr: { name } = {} } = this.props;

        if (!label) {
            return null;
        }

        return (
            <div block="Field" elem="LabelContainer">
                <label
                    htmlFor={ name || `input-${type}` }
                    block="FieldInput"
                    elem="Label"
                >
                    { label }
                    { this.renderRequiredTag() }
                </label>
            </div>
        );
    }

    renderNumber() {
        const {
            attr,
            events,
            setRef,
            isDisabled = false,
            size,
            validationRule,
            isErrorMessage,
            className,
            errorMessagePosition
        } = this.props;

        return (
            <FieldNumberContainer
                attr={ attr }
                events={ events }
                setRef={ setRef }
                isDisabled={ isDisabled }
                size={ size }
                validationRule={ validationRule }
                isErrorMessage={ isErrorMessage }
                className={ className }
                errorMessagePosition={ errorMessagePosition }
            />
        );
    }

    renderSelect() {
        const {
            attr,
            events,
            setRef,
            options,
            isReadOnly,
            isDisabled,
            isLoading,
            heightSize
        } = this.props;

        return (
            <FieldSelectContainer
                attr={ attr }
                events={ events }
                options={ options }
                setRef={ setRef }
                isDisabled={ isDisabled }
                isReadOnly={ isReadOnly }
                isLoading={ isLoading }
                heightSize={ heightSize }
            />
        );
    }

    renderButton() {
        const {
            setRef, attr, events, isDisabled
        } = this.props;
        const { value = __('Submit') } = attr;

        return (
            <button
                ref={ (elem) => setRef(elem) }
                disabled={ isDisabled }
                { ...attr }
                { ...events }
            >
                { value }
            </button>
        );
    }

    renderToggleRequiredIcon() {
        const { validationRule: { isRequired } } = this.props;

        if (isRequired) {
            return <LockIcon size={ XS_KEY } color={ GREEN_KEY } />;
        }

        return null;
    }

    renderCheckboxOrRadio() {
        const {
            type,
            setRef,
            attr,
            attr: { id = '' } = {},
            events: { onChange },
            size,
            events,
            isDisabled,
            label,
            isLoading,
            isToggle,
            fontClass,
            className,
            labelClass,
            validationRule: { isRequired }
        } = this.props;

        const inputEvents = {
            ...events,
            onChange: onChange || noopFn
        };

        if (isLoading) {
            if (isToggle) {
                return (
                 <div block="Field" elem="InputContainer">
                    <TextPlaceholder height="content" length="slider" />
                 </div>
                );
            }

            return (
            <div block="Field" elem="InputContainer" mods={ { isLoading } }>
                <TextPlaceholder height="icon" length="icon" />
                <TextPlaceholder height="paragraph" length="sort" />
            </div>
            );
        }

        return (
            <div block="Field" elem="InputContainer" mods={ { size } }>
                <label
                    htmlFor={ id }
                    block="Field"
                    elem={ isToggle ? 'Toggle' : `${type}Label` }
                    mods={ { size } }
                    className={ `font-normal text-bmText-default ${labelClass}` }
                >
                    <input
                        className={ className }
                        ref={ setRef }
                        disabled={ isDisabled }
                        type={ type }
                        block="Field"
                        elem="Input"
                        { ...attr }
                        { ...inputEvents }
                    />
                    { isToggle && (
                      <span block="Field" elem="Slider" mods={ { size, isRequired } }>
                        { this.renderToggleRequiredIcon() }
                      </span>
                    ) }
                    { !isToggle && (
                        <>
                            <div block="input-control" mods={ { isDisabled } } />
                            <div block="Field" elem="Label" className={ fontClass } mods={ { isDisabled } }>
                                { label }
                            </div>
                        </>
                    ) }
                </label>
            </div>
        );
    }

    renderTextArea() {
        const {
            setRef, attr, events, isDisabled, textareaPlaceholderClass
        } = this.props;

        return (
            <textarea
                className={ `${textareaPlaceholderClass} p-2` }
                ref={ (elem) => setRef(elem) }
                disabled={ isDisabled }
                { ...attr }
                { ...events }
            />
        );
    }

    //#region LABEL/TEXT RENDER
    // Renders validation error messages under field
    renderErrorMessage = (message, key) => (
        <div block="Field" elem="ErrorMessage" key={ key }>{ __(message) }</div>
    );

    renderErrorMessages() {
        const {
            showErrorAsLabel,
            validationResponse,
            attr: { name }
        } = this.props;

        if (!showErrorAsLabel || !validationResponse || validationResponse === true) {
            return null;
        }

        const { errorMessages } = validationResponse;

        if (!errorMessages) {
            return null;
        }

        return (
            <div block="Field" elem="ErrorMessages">
                { errorMessages.map((message, index) => this.renderErrorMessage(message, name + index)) }
            </div>
        );
    }

    // Renders * for required fields
    renderRequiredTag() {
        const { validationRule: { isRequired } } = this.props;

        if (!isRequired) {
            return null;
        }

        return (
            <span block="Field" elem="Label" mods={ { isRequired: true } }>
                *
            </span>
        );
    }

    // Renders fields label under field
    renderSubLabel() {
        const { subLabel } = this.props;

        if (!subLabel) {
            return null;
        }

        return (
            <div block="Field" elem="SubLabelContainer">
                <div block="Field" elem="SubLabel">
                    { subLabel }
                </div>
            </div>
        );
    }
    //#endregion

    renderInputContainer(inputRenderer) {
        const { type } = this.props;
        const needsLabelInInput = [
            FIELD_TYPE.email,
            FIELD_TYPE.text,
            FIELD_TYPE.password,
            FIELD_TYPE.date,
            FIELD_TYPE.dateTime,
            FIELD_TYPE.time,
            FIELD_TYPE.textarea
        ].includes(type);

        if (needsLabelInInput) {
            return inputRenderer();
        }

        return (
            <>
                { inputRenderer() }
            </>
        );
    }

    render() {
        const {
            type, validationResponse, mix, isLast
        } = this.props;
        const inputRenderer = this.renderMap[type];

        const spacingBottom = isLast ? '' : 'mb-4';

        return (
            <div block="Field" elem="Wrapper" className={ spacingBottom } mods={ { type } }>
                <div
                    block="Field"
                    mods={ {
                      type,
                      isValid: validationResponse === true,
                      hasError: validationResponse !== true && Object.keys(validationResponse || {}).length !== 0
                  } }
                    mix={ mix }
                >

                    { this.renderInputContainer(inputRenderer) }
                </div>
                { this.renderErrorMessages() }
                { this.renderSubLabel() }
            </div>
        );
    }
}

export default FieldComponent;
